"use client"

import React,{ useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Send, X, Bot } from "lucide-react"

const PRODUCTS = [
  {
    name: "ROBOTIC ROVER KIT",
    desc: "Build your first autonomous rover and explore obstacle navigation, sensor logic, and motor control hands-on.",
    link: "https://amazon.in/stemkit-alpha",
    icon: "🤖",
  },
  {
    name: "HUMANOID ROBOT KIT",
    desc: "Create a bipedal robot that mimics human movement—integrates AI vision, speech, and programmable motion.",
    link: "https://amazon.in/stemlab-pro",
    icon: "🦾",
  },
  {
    name: "DIY ROBOTIC ARM KIT",
    desc: "Assemble and control a mechanical arm—learn kinematics, servo programming, and pick-place automation.",
    link: "https://stemworld.in/workshops",
    icon: "🦿",
  },
  {
    name: "GEOGRAPHY AI & IOT KIT",
    desc: "Blend Earth science with tech—use IoT sensors and AI models to map, monitor, and interact with environments.",
    link: "https://stemworld.in/workshops",
    icon: "🌍",
  },
  {
    name: "JUNIOR ROBOTICS KIT",
    desc: "Perfect for young minds—snap-together robotics with visual coding to learn fundamentals of logic and control.",
    link: "https://stemworld.in/workshops",
    icon: "🧩",
  },
]

const FAQ_PATTERNS = [
  {
    keywords: [
      "about",
      "stemworld",
      "stem",
      "what is stemworld",
      "tell me about stemworld",
      "who are you",
      "about your company",
      "explain stemworld",
      "company info",
      "company information",
    ],
    answer:
      "🌟 <strong>STEM World</strong> is an innovation-driven ed-tech company founded by alumni of <strong>IIT, NIT, and ISB</strong>. We empower K-12 students with hands-on learning in <strong>Coding, Robotics, AI, and more</strong> through interactive programs, labs, DIY kits, and our immersive platform called <strong>STEMVERSE</strong>. Experience the future of education through real innovation and creation. 🚀",
    threshold: 0.3,
  },
  {
    keywords: [
      "location",
      "where",
      "where are you located",
      "your location",
      "head office",
      "where is stemworld",
      "office location",
      "address",
      "where",
      "headquarters",
      "find you",
      "visit",
      "office address",
    ],
    answer: `📍 <strong>STEM World Location:</strong><br/>
1st Floor, Plot 151, Huda Layout,<br/>
Nallagandla, Serilingampally,<br/>
Hyderabad – 500019, Telangana<br/><br/>
<a href="https://www.google.com/maps/place/STEM+WORLD+Robotics+and+Coding+Centre/@17.4739,78.308117,2073m/data=!3m1!1e3!4m6!3m5!1s0x3bcb9300237ab8bb:0x527c80e6a73ef22f!8m2!3d17.4738998!4d78.3081167!16s%2Fg%2F11vt83kqfj?hl=en&entry=ttu&g_ep=EgoyMDI1MDYwMS4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer" class="inline-block bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-2 rounded-full text-sm font-semibold no-underline hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">📍 View on Google Maps</a>`,
    threshold: 0.4,
  },
  {
    keywords: [
      "how long in business",
      "years active",
      "established",
      "company age",
      "when started",
      "founding",
      "since when",
      "history",
    ],
    answer: "STEMWorld has been empowering learners since 2018, growing rapidly across India.",
    threshold: 0.5,
  },
  {
    keywords: [
      "are your products safe",
      "safety",
      "kid friendly",
      "safe for children",
      "child safety",
      "secure",
      "protection",
      "safety standards",
    ],
    answer: "All our kits are designed with child safety as a top priority and comply with relevant safety standards.",
    threshold: 0.4,
  },
  {
    keywords: [
      "support",
      "help",
      "repair",
      "broken kit",
      "warranty",
      "customer service",
      "support center",
      "assistance",
      "technical support",
      "after sales",
    ],
    answer: "We offer full support and warranty for all our products. Contact us anytime for assistance.",
    threshold: 0.3,
  },
  {
    keywords: [
      "partner",
      "schools",
      "institutions",
      "bulk orders",
      "bulk pricing",
      "collaboration",
      "partnership",
      "school programs",
      "institutional",
    ],
    answer: "We partner with schools and institutions. Bulk pricing and custom programs are available—reach out to us!",
    threshold: 0.4,
  },
  {
    keywords: [
      "what do you offer",
      "your products",
      "what can i buy",
      "what services",
      "what do you sell",
      "what does stemworld provide",
      "offerings",
      "services",
      "products list",
    ],
    answer: "We offer STEM kits, workshops, and online courses focused on innovation, robotics, and AI.",
    threshold: 0.3,
  },
  {
    keywords: ["prices", "cost", "how much", "pricing", "price list", "rates", "charges", "fee", "expense"],
    answer: "Prices vary by product; check each product page for detailed pricing or contact us for quotes.",
    threshold: 0.4,
  },
]

const SECTION_LINKS = {
  about:
    "🌟 <strong>STEM World</strong> is an innovation-driven ed-tech company founded by alumni of <strong>IIT, NIT, and ISB</strong>. We empower K-12 students with hands-on learning in <strong>Coding, Robotics, AI, and more</strong> through interactive programs, labs, DIY kits, and our immersive platform called <strong>STEMVERSE</strong>. Experience the future of education through real innovation and creation. <br/>You can find more about company on 🚀 https://stemworld.in/#/",
  products: "https://stemworld.in/#/product",
  kits: "https://stemworld.in/#/product",
  courses: "https://stemworld.in/#/product",
  contact: "https://stemworld.in/contact",
  demo: "https://stemworld.in/#/",
  pricing: "https://stemworld.in/#/product",
}

function preprocessText(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter((word) => word.length > 2)
}

function calculateKeywordMatch(userWords, keywords) {
  let totalMatches = 0
  let totalKeywords = 0
  for (const keyword of keywords) {
    const keywordWords = preprocessText(keyword)
    totalKeywords += keywordWords.length
    for (const keywordWord of keywordWords) {
      for (const userWord of userWords) {
        if (userWord === keywordWord) {
          totalMatches += 1
        } else if (userWord.length > 4 && keywordWord.length > 4) {
          if (userWord.includes(keywordWord) || keywordWord.includes(userWord)) {
            totalMatches += 0.7
          }
        } else if (userWord.length > 3 && keywordWord.length > 3) {
          const similarity = calculateSimilarity(userWord, keywordWord)
          if (similarity > 0.8) {
            totalMatches += similarity * 0.8
          }
        }
      }
    }
  }
  return totalKeywords > 0 ? totalMatches / totalKeywords : 0
}

function calculateSimilarity(str1, str2) {
  const longer = str1.length > str2.length ? str1 : str2
  const shorter = str1.length > str2.length ? str2 : str1
  if (longer.length === 0) return 1.0
  const editDistance = levenshteinDistance(longer, shorter)
  return (longer.length - editDistance) / longer.length
}

function levenshteinDistance(str1, str2) {
  const matrix = []
  for (let i = 0; i <= str2.length; i++) matrix[i] = [i]
  for (let j = 0; j <= str1.length; j++) matrix[0][j] = j
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j] + 1)
      }
    }
  }
  return matrix[str2.length][str1.length]
}

function detectIntent(userInput) {
  const text = userInput.toLowerCase()
  const userWords = preprocessText(userInput)
  if (text.includes("demo")) {
    return "book_demo"
  } else if (["product", "kit", "course", "workshop", "buy"].some((k) => text.includes(k))) {
    return "show_products"
  } else if (["contact", "call", "phone", "reach", "email", "contac"].some((k) => text.includes(k))) {
    return "contact_us"
  } else if (["about", "section", "page", "link", "navigate", "go to", "visit"].some((k) => text.includes(k))) {
    return "section_link"
  } else if (["hello", "hi", "hey", "greetings", "good morning", "good afternoon"].some((k) => text.includes(k))) {
    return "greeting"
  }
  let bestMatch = null
  let bestScore = 0
  for (const pattern of FAQ_PATTERNS) {
    const matchScore = calculateKeywordMatch(userWords, pattern.keywords)
    const threshold = pattern.threshold || 0.3
    if (matchScore > threshold && matchScore > bestScore) {
      bestScore = matchScore
      bestMatch = pattern
    }
  }
  if (bestMatch) return "company_info_matched"
  return "company_info"
}

function getCompanyInfo(userInput) {
  const userWords = preprocessText(userInput)
  let bestMatch = null
  let bestScore = 0
  for (const pattern of FAQ_PATTERNS) {
    const matchScore = calculateKeywordMatch(userWords, pattern.keywords)
    const threshold = pattern.threshold || 0.3
    if (matchScore > threshold && matchScore > bestScore) {
      bestScore = matchScore
      bestMatch = pattern
    }
  }
  if (bestMatch) return bestMatch.answer
  const text = userInput.toLowerCase()
  let bestMatchScore = 0
  let bestAnswer = null
  for (const entry of FAQ_PATTERNS) {
    for (const phrase of entry.keywords) {
      const score = calculateSimilarity(phrase, text)
      if (score > bestMatchScore && score > 0.6) {
        bestMatchScore = score
        bestAnswer = entry.answer
      }
    }
  }
  if (bestAnswer) return bestAnswer
  return "I'm still learning. Please check https://stemworld.in for more info."
}

function getSectionLink(userInput) {
  const text = userInput.toLowerCase()
  for (const key of Object.keys(SECTION_LINKS)) {
    if (text.includes(key)) {
      return `You can find detailed information here: ${SECTION_LINKS[key]}`
    }
  }
  return "Sorry, I couldn't find that section. Please visit https://stemworld.in for more info."
}

function bookDemo() {
  return "🎯 <b>Excellent choice!</b><br/><br/>Please visit our demo booking page:  https://stemworld.in/#demo<br/><br/>Our free demo sessions are perfect for experiencing our hands-on STEM approach with live coding, robotics, and interactive science experiments!<br/><br/>📞 <b>Quick call:</b> +918121481525/+918121629315"
}

function getLocation() {
  return `📍 <strong>STEM World Location:</strong><br/>
1st Floor, Plot 151, Huda Layout,<br/>
Nallagandla, Serilingampally,<br/>
Hyderabad – 500019, Telangana<br/><br/>
<a href="https://www.google.com/maps/place/STEM+WORLD+Robotics+and+Coding+Centre/@17.4739,78.308117,2073m/data=!3m1!1e3!4m6!3m5!1s0x3bcb9300237ab8bb:0x527c80e6a73ef22f!8m2!3d17.4738998!4d78.3081167!16s%2Fg%2F11vt83kqfj?hl=en&entry=ttu&g_ep=EgoyMDI1MDYwMS4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer" class="inline-block bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-2 rounded-full text-sm font-semibold no-underline hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">📍 View on Google Maps</a>`
}

function showProducts() {
  let response = "🛍️ <b>Here are our top products and services:</b><br/><br/>"
  for (const p of PRODUCTS) {
    response += `${p.icon} <b>${p.name}</b>: ${p.desc}<br/>🛒 <a href="${p.link}" target="_blank">Buy or Learn More</a><br/><br/>`
  }
  return response
}

function contactUs(userInput) {
  const phoneMatch = userInput.match(/\d{10}/)
  const userNumber = phoneMatch ? phoneMatch[0] : "Not provided"
  let response = ""
  if (phoneMatch) {
    response += `✅ <b>Thanks! We've noted your number:</b> ${userNumber}<br/>We'll call you back soon!<br/><br/>`
  }
  response += `📞 <b>You can reach us at:</b> +91-8121481525/+91-8121629315<br/>📧 <b>Email:</b> contact@stemworld.in<br/>🌐 <b>Website:</b>  https://stemworld.in/#/contact<br/><br/><b>Business Hours:</b> Mon-Fri 9AM-6PM, Sat 10AM-4PM`
  return response
}

function getBotResponse(input) {
  const intent = detectIntent(input)
  switch (intent) {
    case "book_demo":
      return bookDemo()
    case "show_products":
      return showProducts()
    case "contact_us":
      return contactUs(input)
    case "section_link":
      return getSectionLink(input)
    case "company_info_matched":
      return getCompanyInfo(input)
    case "company_info":
      return getCompanyInfo(input)
    case "greeting":
      return "Hello! 👋 Great to see you again! How can I help you today?"
    default:
      return "I'm not sure how to help with that. Try asking about demo booking, products, or contact info."
  }
}

// Helper function to format bot messages with HTML
function formatBotMessage(text, theme) {
  return (
    text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n/g, "<br>")
      // Handle regular URLs that are NOT already wrapped in <a> tags
      .replace(
        /(?<!href=")(?<!">)(https?:\/\/[^\s<"]+)(?![^<]*<\/a>)/g,
        `<a href="$1" target="_blank" class="${
          theme === "dark" ? "text-blue-300" : "text-blue-500"
        } underline hover:text-blue-700 break-all">$1</a>`,
      )
  )
}

const MAIN_OPTIONS = [
  { key: "company", label: "About STEM World", icon: "🏢" },
  { key: "demo", label: "Book a Demo", icon: "🎯" },
  { key: "products", label: "View Products", icon: "🛍️" },
  { key: "contact", label: "Contact Us", icon: "📞" },
  { key: "location", label: "Our Location", icon: "📍" },
]

export default function ChatBotModal({ open = true, onClose = () => {} }) {
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: `👋 Hello! Welcome to STEMWorld!<br/><br/>I'm your AI STEM Bot assistant. I'm here to help you explore our exciting world of Science, Technology, Engineering, and Mathematics learning experiences.<br/><br/>How can I assist you today?`,
    },
  ])
  const [showOptions, setShowOptions] = useState(true)
  const [input, setInput] = useState("")
  const [typing, setTyping] = useState(false)
  const [theme, setTheme] = useState("light")
  const chatBoxRef = useRef(null)
  const inactivityTimer = useRef(null)
  const inputRef = useRef(null)

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [open])

  // Scroll to bottom on new message
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight
    }
  }, [messages, typing])

  // Inactivity timer: show options after delay
  useEffect(() => {
    if (!open) return
    clearTimeout(inactivityTimer.current)
    inactivityTimer.current = setTimeout(() => {
      setShowOptions(true)
    }, 6000)
    return () => clearTimeout(inactivityTimer.current)
  }, [messages, open])

  // Reset on modal close
  useEffect(() => {
    if (!open) {
      setInput("")
      setShowOptions(true)
      setTyping(false)
      setMessages([
        {
          sender: "bot",
          text: `👋 Hello! Welcome to STEMWorld!<br/><br/>I'm your AI STEM Bot assistant. I'm here to help you explore our exciting world of Science, Technology, Engineering, and Mathematics learning experiences.<br/><br/>How can I assist you today?`,
        },
      ])
    }
  }, [open])

  // Focus input when modal opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 300)
    }
  }, [open])

  // Toggle theme based on system preference
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    setTheme(mediaQuery.matches ? "dark" : "light")

    const handler = (e) => setTheme(e.matches ? "dark" : "light")
    mediaQuery.addEventListener("change", handler)
    return () => mediaQuery.removeEventListener("change", handler)
  }, [])

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && open) {
        onClose()
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [open, onClose])

  const handleSend = () => {
    if (!input.trim()) return
    setMessages((msgs) => [...msgs, { sender: "user", text: input }])
    setTyping(true)
    setShowOptions(false)
    const userInput = input
    setInput("")
    setTimeout(
      () => {
        const reply = getBotResponse(userInput)
        setMessages((msgs) => [...msgs, { sender: "bot", text: reply }])
        setTyping(false)
        setShowOptions(true)
      },
      Math.random() * 1000 + 500,
    )
  }

  const handleOptionClick = (optionKey) => {
    setShowOptions(false)
    const userText = MAIN_OPTIONS.find((o) => o.key === optionKey)?.label || ""
    setMessages((msgs) => [...msgs, { sender: "user", text: userText }])
    setTyping(true)
    setTimeout(() => {
      let reply = ""
      switch (optionKey) {
        case "demo":
          reply = bookDemo()
          break
        case "products":
          reply = showProducts()
          break
        case "contact":
          reply = contactUs("")
          break
        case "company":
          reply = getCompanyInfo("about stemworld")
          break
        case "location":
          reply = getLocation()
          break
        default:
          reply = "I'm not sure how to help with that."
      }
      setMessages((msgs) => [...msgs, { sender: "bot", text: reply }])
      setTyping(false)
      setShowOptions(true)
    }, 1200)
  }

  // Close modal when clicking backdrop
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-2"
      onClick={handleBackdropClick}
    >
      <motion.div
        initial={{ opacity: 0, y: "100%", scale: 1 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: "100%", scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`flex flex-col overflow-hidden w-full h-full sm:w-[90%] sm:h-[90%] sm:max-w-md sm:max-h-[600px] sm:rounded-xl shadow-2xl ${
          theme === "dark" ? "bg-gray-900 text-white" : "bg-white text-gray-800"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-4 py-3 ${
            theme === "dark" ? "bg-indigo-900" : "bg-gradient-to-r from-indigo-600 to-purple-600"
          } text-white flex-shrink-0`}
        >
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Bot size={24} className="text-white" />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></span>
            </div>
            <h1 className="font-bold text-lg">AIb STEM Bot</h1>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/20 transition-colors"
            aria-label="Close chat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Chat Content */}
        <div className="flex flex-col flex-1 min-h-0">
          {/* Messages */}
          <div
            ref={chatBoxRef}
            className={`flex-1 overflow-y-auto p-4 space-y-4 ${
              theme === "dark" ? "bg-gray-800" : "bg-gray-50"
            } min-h-0`}
            style={{
              scrollBehavior: "smooth",
              overscrollBehavior: "contain",
            }}
          >
            <AnimatePresence initial={false}>
              {messages.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      msg.sender === "user"
                        ? theme === "dark"
                          ? "bg-indigo-600 text-white"
                          : "bg-indigo-500 text-white"
                        : theme === "dark"
                          ? "bg-gray-700 text-white"
                          : "bg-white text-gray-800 shadow-md"
                    }`}
                  >
                    {msg.sender === "user" ? (
                      // For user messages, escape HTML and display as plain text
                      <span>{msg.text}</span>
                    ) : (
                      // For bot messages, allow formatted HTML
                      <span
                        dangerouslySetInnerHTML={{
                          __html: formatBotMessage(msg.text, theme),
                        }}
                      />
                    )}
                  </div>
                </motion.div>
              ))}

              {typing && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className={`rounded-2xl px-4 py-3 ${theme === "dark" ? "bg-gray-700" : "bg-white shadow-md"}`}>
                    <div className="flex space-x-1">
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ repeat: Number.POSITIVE_INFINITY, duration: 0.8, delay: 0 }}
                        className={`w-2 h-2 rounded-full ${theme === "dark" ? "bg-gray-400" : "bg-gray-400"}`}
                      />
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ repeat: Number.POSITIVE_INFINITY, duration: 0.8, delay: 0.2 }}
                        className={`w-2 h-2 rounded-full ${theme === "dark" ? "bg-gray-400" : "bg-gray-400"}`}
                      />
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ repeat: Number.POSITIVE_INFINITY, duration: 0.8, delay: 0.4 }}
                        className={`w-2 h-2 rounded-full ${theme === "dark" ? "bg-gray-400" : "bg-gray-400"}`}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Quick Options */}
            <AnimatePresence>
              {showOptions && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="flex-shrink-0"
                >
                  <div className={`p-3 rounded-lg ${theme === "dark" ? "bg-gray-700" : "bg-white shadow-md"}`}>
                    <h3 className="font-medium mb-2 text-xs">Choose an option:</h3>
                    <div className="grid grid-cols-1 gap-1.5">
                      {MAIN_OPTIONS.map((opt) => (
                        <motion.button
                          key={opt.key}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleOptionClick(opt.key)}
                          className={`flex items-center space-x-2 w-full text-left px-3 py-2 rounded-md transition-colors text-xs ${
                            theme === "dark"
                              ? "bg-gray-800 hover:bg-indigo-900 text-white"
                              : "bg-gray-50 hover:bg-indigo-100 text-gray-800"
                          }`}
                        >
                          <span className="text-sm flex-shrink-0">{opt.icon}</span>
                          <span className="truncate">{opt.label}</span>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Input Area */}
          <div
            className={`p-4 border-t ${
              theme === "dark" ? "border-gray-700 bg-gray-900" : "border-gray-200"
            } flex-shrink-0`}
          >
            <div className="relative flex items-center">
              <input
                ref={inputRef}
                type="text"
                placeholder="Type your message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSend()
                }}
                className={`w-full px-4 py-3 pr-12 rounded-full focus:outline-none text-sm ${
                  theme === "dark"
                    ? "bg-gray-800 text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500"
                    : "bg-gray-100 text-gray-800 placeholder-gray-500 focus:ring-2 focus:ring-indigo-300"
                }`}
              />
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleSend}
                className="absolute right-2 p-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                aria-label="Send message"
              >
                <Send size={16} />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
